import { useState } from 'react';
import { Button, Icon, Link, Tooltip } from '@nimbus-ds/components';
import { ExternalLinkIcon } from '@nimbus-ds/icons';
import { PaymentProviderDto } from '@tiendanube/common';
import {
  customPaymentsIds,
  muvemPagoOrPagoNubeIds,
} from '@tiendanube/common/src/domains/payments/providers/PaymentProvidersCode';
import { PROJECT_SHIVA_TAG } from 'App/featuresFlags';
import { Stack, SubmitButton, useResponsive } from 'commons/components';
import { useGetTags } from 'domains/Auth/hooks';
import dashboardService from 'domains/Dashboard/services';
import useTranslationPayments from 'domains/Payments/useTranslationPayments';
import { OnPaymentProviderActionType } from '../../pages/PaymentProvidersListPage/components/PaymentProviderActionModal';
import { useProvidersAction } from '../useProvidersAction';

interface UseProvidersActionsItemsProps {
  paymentProvider: PaymentProviderDto;
  onAction: OnPaymentProviderActionType;
}

const TRANSLATE_PREFIX = 'paymentProvider';

export const useProvidersActionsItems = ({
  paymentProvider,
  onAction,
}: Readonly<UseProvidersActionsItemsProps>) => {
  const t = useTranslationPayments(TRANSLATE_PREFIX);
  const { isMobile } = useResponsive();
  const isNuvemPagoOrPagoNube = muvemPagoOrPagoNubeIds.includes(
    paymentProvider.id,
  );

  const {
    handleGoToMainPage,
    handleEnable,
    handleCancelInstallation,
    handleGoToCompleteInstallation,
    handleDisable,
    handleGoToSettingPage,
  } = useProvidersAction({
    paymentProvider,
    onAction,
  });
  const {
    id,
    name,
    isActive,
    mustCompleteInstallation,
    isReadonly,
    processingInstallation,
    isProneToScam,
    onboardingIncomplete,
    canBeDeactivated,
  } = paymentProvider;

  const getLeftLink = () => {
    if (processingInstallation) return [];
    if (customPaymentsIds.includes(id)) {
      return [];
    } else if (isNuvemPagoOrPagoNube) {
      if (onboardingIncomplete) {
        return [];
      }
      return [t('actions.seeAccount')];
    } else {
      return [
        <Icon
          key={id}
          color="primary-interactive"
          source={<ExternalLinkIcon />}
        />,
        t('actions.goTo', { name }),
      ];
    }
  };

  const [statusLoadingPaymentsConfig, setStatusLoadingPaymentsConfig] =
    useState<'idle' | 'success' | 'loading' | 'error'>('idle');

  const isLoadingPayments = statusLoadingPaymentsConfig === 'loading';

  const tags = useGetTags();
  const isShivaProject = tags.includes(PROJECT_SHIVA_TAG);

  const paymentTask = async () => {
    setStatusLoadingPaymentsConfig('loading');
    try {
      await dashboardService.updateOnboardingTaskSetupPayments();
      setStatusLoadingPaymentsConfig('success');
      handleGoToSettingPage();
    } catch (error) {
      setStatusLoadingPaymentsConfig('error');
    }
  };

  const getRightLink = () => {
    const shouldShowForNuvemPago =
      isNuvemPagoOrPagoNube && !onboardingIncomplete;

    const shouldHideForProcessing =
      processingInstallation && !shouldShowForNuvemPago;

    if (shouldHideForProcessing) return [];

    if (!isReadonly && !isActive && !mustCompleteInstallation) {
      return (
        <Button
          appearance="primary"
          onClick={handleEnable}
          disabled={isProneToScam}
        >
          {t('actions.enable')}
        </Button>
      );
    } else if (mustCompleteInstallation && !shouldShowForNuvemPago) {
      return (
        <div className="stratus--payment-provider-pending-buttons">
          <Stack gap="4">
            <Link
              as="button"
              appearance="primary"
              textDecoration="none"
              onClick={handleCancelInstallation}
            >
              {t('actions.cancelInstallation')}
            </Link>
            <Button onClick={handleGoToCompleteInstallation}>
              {t('actions.completeInstallation')}
            </Button>
          </Stack>
        </div>
      );
    } else {
      return (
        <Stack gap="4">
          {isActive && !isReadonly && (
            <Tooltip
              content={t('actions.disableBlockedTooltip')}
              position="left"
              arrow
              hidden={canBeDeactivated}
            >
              <Link
                as="button"
                appearance="primary"
                textDecoration="none"
                disabled={!canBeDeactivated}
                data-metric={muvemPagoOrPagoNubeIds.includes(id) || undefined}
                onClick={handleDisable}
              >
                {t('actions.disable')}
              </Link>
            </Tooltip>
          )}
          {(isActive || shouldShowForNuvemPago) && (
            <SubmitButton
              appearance="primary"
              onClick={isShivaProject ? handleGoToSettingPage : paymentTask}
              disabled={isLoadingPayments}
            >
              {isMobile ? t('actions.configureMobile') : t('actions.configure')}
            </SubmitButton>
          )}
        </Stack>
      );
    }
  };

  return {
    getLeftLink,
    getRightLink,
    handleGoToMainPage,
  };
};
